import type { CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '../../i18n';
import { findTerm } from '../glossary/terms';
import { MODULES, type LearnModule, type Lesson, type LessonHit } from './curriculum';

/* --- The curriculum as a table of contents ----------------------------------

   Nothing in Learn is locked and nothing ever was: every lesson has always
   been a plain link. What was wrong was the PRESENTATION — a big "Continue"
   CTA on top and a lone "Next lesson" at the bottom made a reference book
   read like a track you had to walk end to end.

   So this file owns the book's two structural pieces: the flat index used for
   ordering (which is still real — the curriculum builds ideas up, and a reader
   who wants the path should get it) and the module card that lists every
   lesson in it, open by default, each one directly tappable.

   Ordering helpers live here rather than in LearnScreen because they are pure
   curriculum arithmetic with no progress storage in them. LearnScreen
   re-exports them so existing import sites keep working. */

/** Modules in curriculum order: the reading order, never a required order. */
export const ORDERED_MODULES: LearnModule[] = [...MODULES].sort((a, b) => a.order - b.order);

/** Every lesson, flattened, in the same order. */
export const ORDERED_LESSONS: LessonHit[] = ORDERED_MODULES.flatMap((module) =>
  module.lessons.map((lesson) => ({ module, lesson })),
);

/** First lesson, in curriculum order, that has not been finished. */
export function nextUnfinishedLesson(done: ReadonlySet<string>): LessonHit | undefined {
  return ORDERED_LESSONS.find((hit) => !done.has(hit.lesson.id));
}

/** The lesson after this one, crossing module boundaries. */
export function lessonAfter(id: string): LessonHit | undefined {
  const at = ORDERED_LESSONS.findIndex((hit) => hit.lesson.id === id);
  return at >= 0 ? ORDERED_LESSONS[at + 1] : undefined;
}

/** The lesson before this one, crossing module boundaries. */
export function lessonBefore(id: string): LessonHit | undefined {
  const at = ORDERED_LESSONS.findIndex((hit) => hit.lesson.id === id);
  return at > 0 ? ORDERED_LESSONS[at - 1] : undefined;
}

/** Done-count restricted to lessons that still exist in the curriculum. */
export function learnDoneCount(done: ReadonlySet<string>): number {
  return ORDERED_LESSONS.reduce((n, hit) => n + (done.has(hit.lesson.id) ? 1 : 0), 0);
}

/* --- Search ---------------------------------------------------------------- */

/**
 * Hyphens, dots and underscores collapse to spaces so a glossary id
 * (`rahu-kaal`, `d9`) and the way a person types it ("rahu kaal") land on the
 * same string.
 */
function normalise(value: string): string {
  return value.toLowerCase().replace(/[-_.]+/g, ' ').replace(/\s+/g, ' ').trim();
}

/**
 * One haystack per lesson: its own title, the module it sits in, and every
 * glossary term it teaches — id, display title and all the alternate spellings
 * that term already collects. Searching "kundli milan" or "sade sati" or
 * "d9" therefore finds the lesson that covers it even when the lesson title
 * never uses that word, which is the whole point of a book's index.
 */
const SEARCH_INDEX: Array<{ id: string; hay: string }> = ORDERED_LESSONS.map(
  ({ module, lesson }) => {
    const parts: string[] = [lesson.title, module.title, module.tagline];
    for (const termId of lesson.terms) {
      const term = findTerm(termId);
      if (term) parts.push(term.id, term.title, ...(term.aliases ?? []));
      else parts.push(termId);
    }
    return { id: lesson.id, hay: normalise(parts.join(' | ')) };
  },
);

/**
 * Lesson ids matching a query, or `null` for an empty query — `null` means
 * "not searching" and is deliberately distinct from an empty Set, which means
 * "searched, found nothing".
 *
 * Every whitespace-separated token has to appear, so typing more words narrows
 * rather than widens.
 */
export function searchLessonIds(query: string): ReadonlySet<string> | null {
  const q = normalise(query);
  if (!q) return null;
  const tokens = q.split(' ');
  const hits = new Set<string>();
  for (const entry of SEARCH_INDEX) {
    if (tokens.every((token) => entry.hay.includes(token))) hits.add(entry.id);
  }
  return hits;
}

/* --- Rendering -------------------------------------------------------------- */

const HEADER_BUTTON: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 10,
  width: '100%',
  minHeight: 44,
  padding: 0,
  background: 'transparent',
  border: 'none',
  color: 'var(--text)',
  font: 'inherit',
  textAlign: 'left',
  cursor: 'pointer',
};

export interface ModuleContentsProps {
  module: LearnModule;
  /** Lessons to list — the module's own, or the subset a search matched. */
  lessons: readonly Lesson[];
  done: ReadonlySet<string>;
  open: boolean;
  onToggle: () => void;
}

/**
 * One chapter of the contents page: the module header, and every lesson in it
 * as a direct link with its length and a done tick.
 *
 * `data-mode` is what makes the screen read Indian-first without a word of
 * copy: ten consecutive vedic modules render in warm saffron, the single
 * western module in cool cyan, and shared modules stay on the app accent.
 */
export function ModuleContents({ module, lessons, done, open, onToggle }: ModuleContentsProps) {
  const { t } = useI18n();
  const moduleDone = module.lessons.filter((lesson) => done.has(lesson.id)).length;
  // The one opted-in tradition. Marked in the header as well as coloured,
  // because colour alone is not an accessible way to say "this is optional".
  const optional = module.system === 'western';

  return (
    <div
      className="card"
      data-mode={module.system === 'shared' ? undefined : module.system}
      style={{
        padding: 14,
        borderColor: optional ? 'var(--mode-line)' : undefined,
        borderStyle: optional ? 'dashed' : undefined,
      }}
    >
      <button
        type="button"
        aria-expanded={open}
        aria-controls={`module-lessons-${module.id}`}
        onClick={onToggle}
        style={HEADER_BUTTON}
      >
        <span style={{ flex: 1, minWidth: 0 }}>
          <span className="wrap" style={{ marginBottom: 6 }}>
            <span className="mode-chip">{t('learn.module', { n: module.order })}</span>
            {optional && <span className="mode-chip">{t('learn.optional')}</span>}
          </span>
          <span className="h3" style={{ display: 'block' }}>
            {module.title}
          </span>
          <span className="tiny muted" style={{ display: 'block', lineHeight: 1.5, marginTop: 2 }}>
            {module.tagline}
          </span>
        </span>
        {/* Count last in DOM order on purpose: this button's accessible name is
            computed from its contents, and "Module 1" immediately followed by
            "0 of 4 lessons" is announced as the garbled "Module 10 of 4". */}
        <span
          className="col"
          style={{ gap: 6, alignItems: 'flex-end', flexShrink: 0 }}
        >
          <span className="tiny faint" style={{ whiteSpace: 'nowrap' }}>
            {t('learn.progress', { done: moduleDone, total: module.lessons.length })}
          </span>
          <span
            aria-hidden
            className="faint"
            style={{
              lineHeight: 1.4,
              transform: open ? undefined : 'rotate(-90deg)',
              transition: 'transform 0.18s ease',
            }}
          >
            ▾
          </span>
        </span>
      </button>

      {open && (
        <div id={`module-lessons-${module.id}`} className="col" style={{ gap: 0 }}>
          <div className="mode-rule" style={{ margin: '10px 0 2px' }} />
          {lessons.map((lesson, i) => {
            const finished = done.has(lesson.id);
            return (
              <Link
                key={lesson.id}
                to={`/learn/${lesson.id}`}
                style={{ color: 'inherit', display: 'block' }}
              >
                {/* No rule under the last row — a trailing hairline inside a
                    card reads as a list that got cut off. */}
                <div
                  className="list-item"
                  style={i === lessons.length - 1 ? { borderBottom: 'none' } : undefined}
                >
                  {/* role="img" + a label is what makes the tick mean
                      something to a screen reader; a bare aria-hidden glyph
                      left "done" as a sighted-only fact. The empty circle
                      stays silent — nothing read is the default state. */}
                  <span
                    aria-hidden={finished ? undefined : true}
                    role={finished ? 'img' : undefined}
                    aria-label={finished ? t('common.done') : undefined}
                    className={finished ? undefined : 'faint'}
                    style={{ flexShrink: 0, color: finished ? 'var(--ok)' : undefined }}
                  >
                    {finished ? '✓' : '○'}
                  </span>
                  <span style={{ flex: 1, lineHeight: 1.45 }}>{lesson.title}</span>
                  <span className="tiny faint" style={{ flexShrink: 0 }}>
                    {t('learn.minutes', { n: lesson.minutes })}
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
